import { useEffect, useRef, useState } from 'react';
import { Editor } from 'pandadoc-editor';
import { api } from '../api/client';
import { isCompleted, isDraft, isProcessing, pandaDocUrl, statusLabel } from '../config/docStatus';
import type { DateField, EditorTokenResponse, GhlContext } from '../types';

type Props = {
  ghl: GhlContext;
  documentId: string;
  onBack: () => void;
};

/**
 * Mounts the official PandaDoc embedded editor (`pandadoc-editor@1.0.1`) for a
 * single document, authenticated with an editing-session E-Token.
 *
 *   const editor = new Editor(elementId, { token });
 *   editor.on('content.rendered', cb); await editor.open(); editor.destroy();
 *
 * The constructor REPLACES the element with the given id, so we hand it a
 * dedicated child node created outside React's control. The package's
 * width/height config can leave the resulting iframe under-sized, so we also
 * force the iframe (and any wrapper it inserts) to fill our container.
 */
const HOST_ID = 'pd-editor-host';

/**
 * Permissions the PandaDoc editor frame needs but is never granted.
 *
 * `pandadoc-editor@1.0.1` builds its iframe with neither `sandbox` nor `allow`
 * (`dist/editor.js`: createElement('iframe'), set src/style/id, done). Under
 * Permissions Policy every gated feature defaults to `self`, so a CROSS-ORIGIN
 * child that was granted nothing has the clipboard and the camera switched off
 * — which is exactly what the editor's own "Click to upload an image" zone
 * reaches for when a rep pastes a screenshot or shoots a job photo on a tablet.
 * We cannot hand on more than GHL granted us in the first place, but leaving
 * the whole list empty was ours to fix.
 */
const EDITOR_ALLOW = 'clipboard-read; clipboard-write; camera; fullscreen';

/**
 * Grant them the moment the frame appears, BEFORE it navigates.
 *
 * Permissions Policy is fixed when the frame's navigation commits, and an
 * iframe that already carries `src` starts loading as soon as it is inserted.
 * A MutationObserver callback is a microtask, so it still runs ahead of that
 * navigation task — setting the attribute from the 150ms fit poll alone would
 * usually be too late.
 */
function watchForEditorFrame(container: HTMLElement): MutationObserver {
  const observer = new MutationObserver(() => {
    const frame = container.querySelector('iframe');
    if (frame && !frame.hasAttribute('allow')) frame.setAttribute('allow', EDITOR_ALLOW);
  });
  observer.observe(container, { childList: true, subtree: true });
  return observer;
}

type Phase = 'token' | 'mounting' | 'ready';

function fitEditorFrame(container: HTMLElement): HTMLIFrameElement | null {
  const frame = container.querySelector('iframe');
  if (!frame) return null;
  if (!frame.hasAttribute('allow')) frame.setAttribute('allow', EDITOR_ALLOW);
  Object.assign(frame.style, { width: '100%', height: '100%', border: 'none', display: 'block' });
  let p = frame.parentElement;
  while (p && p !== container) {
    p.style.width = '100%';
    p.style.height = '100%';
    p = p.parentElement;
  }
  return frame as HTMLIFrameElement;
}

export function EditorPage({ ghl, documentId, onBack }: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [tokenInfo, setTokenInfo] = useState<EditorTokenResponse | null>(null);
  const [phase, setPhase] = useState<Phase>('token');
  const [error, setError] = useState<string | null>(null);
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [hintDismissed, setHintDismissed] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  // Bumping this remounts the editor so freshly-placed photos actually appear;
  // the PandaDoc frame has no "reload content" command of its own.
  const [reloadNonce, setReloadNonce] = useState(0);

  // 1. Mint a fresh editing-session E-Token for this document.
  //    A file-upload document becomes editable (draft) asynchronously, so the
  //    editing-session mint can fail — or come back still `uploaded` — for the
  //    first few seconds. Retry a bounded number of times before surfacing the
  //    error. Template documents are draft immediately and succeed on the first
  //    attempt.
  useEffect(() => {
    let cancelled = false;
    setPhase('token');
    setError(null);
    setTokenInfo(null);

    const MAX_ATTEMPTS = 6;
    const RETRY_MS = 2500;
    let attempt = 0;
    let timer: number | undefined;

    const tryToken = () => {
      api
        .getEditorToken(documentId, ghl)
        .then((r) => {
          if (cancelled) return;
          // Still converting an upload — wait for it to flip to draft rather
          // than showing the "already sent" panel for a brand-new file.
          if (isProcessing(r.status) && ++attempt < MAX_ATTEMPTS) {
            timer = window.setTimeout(tryToken, RETRY_MS);
            return;
          }
          setTokenInfo(r);
          setPhase('mounting');
        })
        .catch((e) => {
          if (cancelled) return;
          if (++attempt < MAX_ATTEMPTS) {
            timer = window.setTimeout(tryToken, RETRY_MS);
          } else {
            setError(toMessage(e));
          }
        });
    };
    tryToken();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [documentId, ghl]);

  // PandaDoc locks a document the moment it leaves draft: the editing-session
  // token still mints, but the editor refuses to render it (empty
  // `document.loading.error`). Show a plain explanation instead of mounting an
  // editor that cannot work. An unknown status falls through to the editor —
  // the old behaviour — so a details hiccup never blocks a real draft.
  const docStatus = tokenInfo?.status ?? null;
  const locked = !!docStatus && !isDraft(docStatus);

  // 2. Mount the PandaDoc editor once we have a token and a container.
  useEffect(() => {
    if (!tokenInfo || locked || !wrapperRef.current) return;

    // A remount (new photos, new dates) empties the frame first; keep the
    // loading line up meanwhile so the rep does not stare at a blank page.
    setPhase('mounting');
    const wrapper = wrapperRef.current;
    wrapper.innerHTML = '';
    const host = document.createElement('div');
    host.id = HOST_ID;
    host.style.width = '100%';
    host.style.height = '100%';
    wrapper.appendChild(host);

    let destroyed = false;
    let editor: Editor | null = null;

    // Installed BEFORE the SDK runs, so the frame is granted its permissions
    // the instant it lands in the DOM and before it navigates.
    const frameWatch = watchForEditorFrame(wrapper);

    // The editor inserts its iframe asynchronously; poll briefly, size it to
    // fill, and hide our loading overlay as soon as it appears.
    let tries = 0;
    const fitTimer = window.setInterval(() => {
      if (destroyed) { window.clearInterval(fitTimer); return; }
      if (fitEditorFrame(wrapper)) { setPhase('ready'); window.clearInterval(fitTimer); }
      else if (++tries > 40) window.clearInterval(fitTimer);
    }, 150);

    const onResize = () => fitEditorFrame(wrapper);
    window.addEventListener('resize', onResize);

    try {
      editor = new Editor(HOST_ID, {
        token: tokenInfo.token,
        width: '100%',
        height: '100%',
        // The SDK defaults `fieldPlacementOnly` to TRUE when it is not an
        // explicit boolean — that mode hides every content block and leaves the
        // native editing toolbar empty (only the fillable-fields rail shows).
        // Reps need the full editor, so turn it off explicitly.
        fieldPlacementOnly: false,
        // The Quote builder and the classic Pricing Table were disabled here in
        // efa9147 because "Edit quote" threw inside the embed iframe — with them
        // off the block still rendered and still accepted keystrokes, so a rep
        // could type an invoice amount that was never committed (a payment
        // request went out at $0 on 2026-08-01). PandaDoc support has since fixed
        // the embedded quote builder, so both blocks are editable again and the
        // amount is set right here.
        blocks: {
          quote: { visible: true },
          pricingTable: { visible: true },
        },
        debugMode: import.meta.env.DEV,
      });

      editor
        .on('content.rendered', () => { if (!destroyed) { setPhase('ready'); fitEditorFrame(wrapper); } })
        .on('document.loading.error', (payload) => {
          // PandaDoc sends an empty payload here when it simply refuses the
          // document (not a draft any more, deleted, or another workspace).
          const detail = toMessage(payload);
          if (!destroyed) {
            setError(
              detail && detail !== '{}'
                ? 'PandaDoc could not load this document. ' + detail
                : 'PandaDoc could not load this document — it may have been sent or deleted since this list was ' +
                  'loaded. Go back and refresh the contact, or open it in PandaDoc.',
            );
          }
        });

      void editor.open()
        .then(() => { if (!destroyed) fitEditorFrame(wrapper); })
        .catch((e) => { if (!destroyed) setError(toMessage(e)); });
    } catch (e) {
      setError(toMessage(e));
    }

    return () => {
      destroyed = true;
      frameWatch.disconnect();
      window.clearInterval(fitTimer);
      window.removeEventListener('resize', onResize);
      try { editor?.destroy(); } catch { /* editor may already be torn down */ }
      wrapper.innerHTML = '';
    };
  }, [tokenInfo, locked, reloadNonce]);

  // ----- Job photos: upload through our backend, not the editor's own zone. --
  // The zone is there and we deliberately enabled it (fieldPlacementOnly:false),
  // but its uploader posts the bytes from the browser to a third-party service,
  // so a blocker or a corporate filter kills it with nothing shown to the rep —
  // and it rejects the .heic an iPhone shoots by default. This path converts on
  // the server and hands PandaDoc a URL, so neither can happen.
  async function handlePhotoPick(list: FileList | null) {
    const photos = Array.from(list || []);
    if (!photos.length) return;
    setPhotoBusy(true);
    setPhotoErr(null);
    setPhotoNote(null);
    try {
      const r = await api.addPhotos(documentId, photos, ghl);
      const parts = [`Added ${r.placed.length} photo${r.placed.length === 1 ? '' : 's'}`];
      if (r.skipped) parts.push(`${r.skipped} did not fit — this document has no free slots left`);
      else if (r.remaining) parts.push(`room for ${r.remaining} more`);
      setPhotoNote(parts.join(' · '));
      setReloadNonce((n) => n + 1); // re-render the document with the photos in it
    } catch (e) {
      setPhotoErr(toMessage(e));
    } finally {
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = ''; // allow re-picking the same file
    }
  }

  // ----- Contract dates: a native date input instead of the editor's boxes. ---
  // The embedded editor's first click on a date box only selects the field; the
  // calendar needs a second, separate click (a double click does nothing), and
  // on an iPad a tap selects or drags the field instead, so reps report they
  // "cannot pick a date" on both (2026-09-15). An <input type="date"> opens the
  // system picker; the backend writes the value into the draft and the editor
  // is reloaded to show it. Only merge-named dates can be offered.
  const [dateFields, setDateFields] = useState<DateField[]>([]);
  const [datesOpen, setDatesOpen] = useState(false);
  const [datesDraft, setDatesDraft] = useState<Record<string, string>>({});
  const [datesPhase, setDatesPhase] = useState<'idle' | 'loading' | 'saving'>('idle');
  const [datesErr, setDatesErr] = useState<string | null>(null);
  const [datesNote, setDatesNote] = useState<string | null>(null);
  const [datesHintDismissed, setDatesHintDismissed] = useState(false);
  const datesSavedAt = useRef(0);
  const emptyDates = dateFields.filter((f) => !f.value).length;

  useEffect(() => {
    setDateFields(tokenInfo?.dateFields ?? []);
  }, [tokenInfo]);

  function openDates() {
    const fill = (fields: DateField[]) => setDatesDraft(Object.fromEntries(fields.map((f) => [f.name, f.value ?? ''])));
    fill(dateFields);
    setDatesErr(null);
    setDatesNote(null);
    setDatesOpen(true);
    // The rep may have picked a date inside the document since the editor
    // loaded, so read the current values -- unless we just wrote them, because
    // PandaDoc's details lag a write by a second or two.
    if (Date.now() - datesSavedAt.current < 10_000) return;
    setDatesPhase('loading');
    api
      .getDocumentDates(documentId, ghl)
      .then((r) => {
        setDateFields(r.dateFields);
        fill(r.dateFields);
      })
      .catch((e) => setDatesErr(toMessage(e)))
      .finally(() => setDatesPhase('idle'));
  }

  async function saveDates() {
    const changed: Record<string, string | null> = {};
    for (const f of dateFields) {
      const next = datesDraft[f.name] ?? '';
      if (next !== (f.value ?? '')) changed[f.name] = next || null;
    }
    if (!Object.keys(changed).length) {
      setDatesOpen(false);
      return;
    }
    setDatesPhase('saving');
    setDatesErr(null);
    try {
      const r = await api.setDocumentDates(documentId, changed, ghl);
      datesSavedAt.current = Date.now();
      setDateFields(r.dateFields);
      setDatesOpen(false);
      setDatesNote('Dates saved — reloading the document to show them.');
      setReloadNonce((n) => n + 1);
    } catch (e) {
      setDatesErr(toMessage(e));
    } finally {
      setDatesPhase('idle');
    }
  }

  // ----- Pre-send modal: rename the document + write the email message. -----
  // MFR titles every document "Customer Name / …" and the "Signed documents"
  // tab finds docs by searching PandaDoc for the customer name in the title —
  // so the customer part stays locked and only the tail is editable.
  const originalName = tokenInfo?.name || '';
  const nameParts = splitDocName(originalName);
  const [modalOpen, setModalOpen] = useState(false);
  const [nameSuffix, setNameSuffix] = useState('');
  const [message, setMessage] = useState('');

  function openSendModal() {
    if (sendState !== 'idle') return;
    setNameSuffix(nameParts.suffix);
    setMessage('');
    setModalOpen(true);
  }

  async function handleSend() {
    if (sendState !== 'idle') return;
    const finalName = nameSuffix.trim() ? (nameParts.prefix + nameSuffix.trim()) : '';
    setModalOpen(false);
    setSendState('sending');
    setSendErr(null);
    try {
      await api.sendDocument(documentId, ghl, {
        // Only rename when the user actually changed something.
        name: finalName && finalName !== originalName ? finalName : undefined,
        message: message.trim() || undefined,
      });
      setSendState('sent');
    } catch (e) {
      setSendState('idle');
      setSendErr(toMessage(e));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: '1px solid #e5e7eb', background: 'white', flex: '0 0 auto' }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#2563eb' }}>← Back</button>
        <span style={{ fontSize: 13, color: '#6b7280' }}>Document {documentId}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          {tokenInfo && !locked && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              session expires {new Date(tokenInfo.expiresAt).toLocaleTimeString()}
            </span>
          )}
          {/* Photos: our own upload path, not the editor's built-in zone. */}
          {!locked && (
            <>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={(e) => void handlePhotoPick(e.target.files)}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={photoBusy}
                title="Photos are converted here, so pictures straight off a phone work too"
                style={{
                  padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13,
                  fontWeight: 600, background: 'white', color: '#374151',
                  cursor: photoBusy ? 'default' : 'pointer', opacity: photoBusy ? 0.6 : 1,
                }}
              >
                {photoBusy ? 'Adding photos…' : 'Add photos'}
              </button>
            </>
          )}

          {!locked && dateFields.length > 0 && (
            <button
              onClick={openDates}
              title="Set the contract dates with this device's own date picker"
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13,
                fontWeight: 600, background: 'white', color: '#374151', cursor: 'pointer',
              }}
            >
              {emptyDates ? `Dates (${emptyDates} empty)` : 'Dates'}
            </button>
          )}

          {/* Sending is only possible from a draft — a locked doc is already out. */}
          {!locked && (
            <button
              onClick={openSendModal}
              disabled={sendState !== 'idle'}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600,
                color: 'white', cursor: sendState === 'idle' ? 'pointer' : 'default',
                background: sendState === 'sent' ? '#16a34a' : '#2563eb', opacity: sendState === 'sending' ? 0.7 : 1,
              }}
            >
              {sendState === 'sending' ? 'Sending…' : sendState === 'sent' ? '✓ Sent' : 'Send for signature'}
            </button>
          )}
        </div>
      </header>

      {sendErr && (
        <div style={{ padding: '6px 16px', background: '#fef2f2', color: '#991b1b', fontSize: 13, flex: '0 0 auto' }}>
          Send failed: {sendErr}
        </div>
      )}

      {photoErr && (
        <div style={{ padding: '6px 16px', background: '#fef2f2', color: '#991b1b', fontSize: 13, flex: '0 0 auto' }}>
          Photos: {photoErr}
        </div>
      )}

      {photoNote && !photoErr && (
        <div style={{ padding: '6px 16px', background: '#f0fdf4', color: '#166534', fontSize: 13, flex: '0 0 auto' }}>
          {photoNote}
        </div>
      )}

      {datesNote && (
        <div style={{ padding: '6px 16px', background: '#f0fdf4', color: '#166534', fontSize: 13, flex: '0 0 auto' }}>
          {datesNote}
        </div>
      )}

      {/* While a date is still empty: point at the form that works with a mouse
          and a finger alike, instead of the date boxes inside the document. */}
      {!locked && !datesHintDismissed && emptyDates > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: '#fffbeb', color: '#92400e', fontSize: 12.5, flex: '0 0 auto' }}>
          <span>
            Set the contract dates with the <strong>Dates</strong> button above — the date boxes inside the
            document only open on a second click, and rarely on a tablet.
          </span>
          <button
            onClick={() => setDatesHintDismissed(true)}
            aria-label="Dismiss"
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 15, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>
      )}

      {/* Payment requests only, and only while the amount is still zero: the
          pricing grid takes keystrokes it never saves, so a rep can send a $0
          invoice without noticing. One dismissible line — no modal. */}
      {!locked && !hintDismissed && tokenInfo?.hasPricing && (tokenInfo.total ?? 0) <= 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: '#fffbeb', color: '#92400e', fontSize: 12.5, flex: '0 0 auto' }}>
          <span>
            Amount is still $0 — set it with the pricing block’s <strong>Edit quote</strong> button.
            Typing straight into the table does not save.
          </span>
          <button
            onClick={() => setHintDismissed(true)}
            aria-label="Dismiss"
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 15, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>
      )}

      {datesOpen && (
        <div
          onClick={() => datesPhase !== 'saving' && setDatesOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 50 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 10, padding: 20, width: 'min(420px, calc(100vw - 32px))', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Contract dates</h2>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
              {datesPhase === 'loading' ? 'Loading the current dates…' : 'Saved straight into the document.'}
            </p>

            {dateFields.map((f) => (
              <div key={f.name} style={{ marginBottom: 12 }}>
                <label
                  htmlFor={`date-${f.name}`}
                  style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}
                >
                  {f.label}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    id={`date-${f.name}`}
                    type="date"
                    value={datesDraft[f.name] ?? ''}
                    disabled={datesPhase !== 'idle'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDatesDraft((d) => ({ ...d, [f.name]: v }));
                    }}
                    // 16px: iOS Safari zooms the whole page into any smaller input on focus.
                    style={{ flex: 1, minWidth: 0, fontSize: 16, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', color: '#111827' }}
                  />
                  {datesDraft[f.name] && (
                    <button
                      onClick={() => setDatesDraft((d) => ({ ...d, [f.name]: '' }))}
                      disabled={datesPhase !== 'idle'}
                      style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: '4px 2px' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            ))}

            {datesErr && (
              <p style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', padding: '6px 10px', borderRadius: 6, margin: '0 0 12px' }}>
                {datesErr}
              </p>
            )}
            <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '0 0 16px' }}>
              Dates not listed here are filled in on the document itself.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setDatesOpen(false)}
                disabled={datesPhase === 'saving'}
                style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => void saveDates()}
                disabled={datesPhase !== 'idle'}
                style={{
                  padding: '7px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: 'white', fontSize: 13,
                  fontWeight: 600, cursor: datesPhase === 'idle' ? 'pointer' : 'default', opacity: datesPhase === 'idle' ? 1 : 0.7,
                }}
              >
                {datesPhase === 'saving' ? 'Saving…' : 'Save dates'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 50 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 10, padding: 20, width: 'min(480px, calc(100vw - 32px))', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Send for signature</h2>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Document name
            </label>
            <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', marginBottom: 4 }}>
              {nameParts.prefix && (
                <span style={{ display: 'flex', alignItems: 'center', padding: '7px 4px 7px 10px', background: '#f3f4f6', color: '#6b7280', fontSize: 13, whiteSpace: 'nowrap' }}>
                  {nameParts.prefix}
                </span>
              )}
              <input
                value={nameSuffix}
                onChange={(e) => setNameSuffix(e.target.value)}
                placeholder="e.g. Work Order #2 — ABC Plumbing"
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', padding: '7px 10px', fontSize: 13 }}
              />
            </div>
            <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '0 0 14px' }}>
              This becomes the PDF filename in Google Drive. The customer name stays in the title so the document
              remains findable under Signed documents.
            </p>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Message to the recipient
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Please review and sign your document from Miami First Remodeling."
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '4px 0 16px' }}>
              Included in the email with the signing link. Leave empty to use the default text above.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {locked ? (
        <div style={{ padding: 24 }}>
          <div style={{ maxWidth: 560, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
              This document has already been sent
            </h2>
            <p style={{ fontSize: 13.5, color: '#4b5563', margin: '0 0 4px' }}>
              <strong>{tokenInfo?.name || documentId}</strong> — status <strong>{statusLabel(docStatus)}</strong>.
            </p>
            <p style={{ fontSize: 13.5, color: '#4b5563', margin: '0 0 16px' }}>
              PandaDoc locks a document once it leaves draft, so it can no longer be opened in the editor.
              You can still view it, and download the PDF once everyone has signed.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <a href={pandaDocUrl(documentId)} target="_blank" rel="noreferrer" style={primaryLink}>
                View in PandaDoc ↗
              </a>
              {isCompleted(docStatus) && (
                <button
                  onClick={() => api.downloadDocument(documentId, ghl).catch((e) => setError(toMessage(e)))}
                  style={{ ...ghostLink, cursor: 'pointer' }}
                >
                  Download signed PDF
                </button>
              )}
              <button onClick={onBack} style={{ ...ghostLink, cursor: 'pointer' }}>Back to documents</button>
            </div>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '16px 0 0' }}>
              Need to change it? In PandaDoc, use the document’s ⋯ menu → <em>Move to draft</em> (this voids any
              signatures already collected), or start a fresh document from the template.
            </p>
          </div>
        </div>
      ) : error ? (
        <div style={{ padding: 24, color: '#991b1b', background: '#fef2f2' }}>
          <strong>Editor failed to load</strong>
          <p style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{error}</p>
        </div>
      ) : (
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {phase !== 'ready' && (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#6b7280', pointerEvents: 'none' }}>
              {phase === 'token' ? 'Preparing document…' : 'Loading PandaDoc editor…'}
            </div>
          )}
          <div ref={wrapperRef} style={{ position: 'absolute', inset: 0 }} />
        </div>
      )}
    </div>
  );
}

const primaryLink: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 6,
  border: '1px solid #2563eb',
  background: '#2563eb',
  color: 'white',
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
};

const ghostLink: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  background: 'white',
  color: '#374151',
  fontSize: 13,
  textDecoration: 'none',
};

// MFR document names look like "Customer Name / <tail>". Lock the customer
// part (everything up to and including the first " / ") and let the rep edit
// only the tail. Names without that pattern are fully editable.
function splitDocName(full: string): { prefix: string; suffix: string } {
  const i = full.indexOf(' / ');
  if (i > 0) return { prefix: full.slice(0, i + 3), suffix: full.slice(i + 3) };
  return { prefix: '', suffix: full };
}

function toMessage(e: unknown): string {
  if (!e) return '';
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try { return JSON.stringify(e); } catch { return String(e); }
}
